<template>
  <figure class="chart-container relative" data-qa="bar-chart">
    <Bar
      :options="computedChartOptions"
      :data="computedChartData"
      :styles="styles"
    />
    <figcaption class="hidden-caption">{{ ariaLabel }}</figcaption>

    <VcLayout
      v-if="isEmpty"
      class="stack-top empty-overlay">
        <slot name="emptyStateArea">
            <VcLayout align-center>
              <VcEmptyState
                :title="$t('common.charts.empty_state')"
                :subtitle="$t('common.charts.empty_state')"
                :aria-label="$t('common.charts.empty_state')"
                :image="require('@/assets/images/no_bar_chart_data.svg')"
                />
            </VcLayout>
        </slot>
    </VcLayout>
  </figure>
</template>

<script>
import { Bar } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from 'chart.js';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
);

// https://www.chartjs.org/docs/latest/general/fonts.html
ChartJS.defaults.font.family = 'Montserrat';

export default {
  name: 'BarChart',
  components: {
    Bar,
  },
  props: {
    styles: {
      type: Object,
      default: () => {},
    },
    legendDisplay: {
      type: Boolean,
      required: false,
    },
    yAxisMax: {
      type: Number,
      required: false,
    },
    yAxisStepSize: {
      type: Number,
      required: false,
    },
    yAxisGridLineDisplay: {
      type: Boolean,
      required: false,
    },
    xAxisGridLineDisplay: {
      type: Boolean,
      required: false,
    },
    // API for custom tooltip text
    // https://www.chartjs.org/docs/latest/configuration/tooltip.html#label-callback
    tooltipCallback: {
      type: Function,
      required: false,
    },
    chartOptions: {
      type: Object,
      required: false,
    },
    chartData: {
      type: Object,
      required: true,
    },
    // https://www.chartjs.org/docs/latest/charts/bar.html#borderradius
    barThickness: {
      type: Number,
      default: 10,
    },
    // https://www.chartjs.org/docs/latest/charts/bar.html#borderradius
    maxBarThickness: {
      type: Number,
      default: 15,
    },
    // https://www.chartjs.org/docs/latest/charts/bar.html#borderradius
    borderRadius: {
      type: Number,
      default: 8,
    },
    barBackgroundColor: {
      type: String,
      default: '#4CAF50',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    // https://www.chartjs.org/docs/latest/configuration/tooltip.html#tooltip-callbacks
    tooltipTitleCallback: {
      type: Function,
    },
    tooltipBackgroundColor: {
      type: String,
      default: 'rgba(97, 97, 97, 0.90)',
    },
    // https://www.chartjs.org/docs/latest/samples/scale-options/ticks.html
    yAxisTickCallback: {
      type: Function,
      required: false,
    },
  },
  computed: {
    isEmpty() {
      return this.chartData?.datasets?.every((dataset) => (
        dataset.data?.every((value) => !value)
      )) ?? true;
    },
    computedChartOptions() {
      const options = { ...this.chartOptions };

      if (!options.scales) {
        options.scales = {};
      }

      options.scales.x = {
        ...options.scales.x,
        grid: {
          display: this.xAxisGridLineDisplay,
          ...options.scales.x?.grid,
        },
      };

      options.scales.y = {
        ...options.scales.y,
        grid: {
          display: this.yAxisGridLineDisplay,
          ...options.scales.y?.grid,
        },
        max: this.yAxisMax ?? options.scales.y?.max,
        ticks: {
          stepSize: this.yAxisStepSize ?? options.scales.y?.ticks?.stepSize,
          callback: this.yAxisTickCallback,
          ...options.scales.y?.ticks,
        },
      };

      options.animations = {
        x: {
          duration: 0,
        },
        y: {
          from: (context) => {
            if (context.type !== 'data') return undefined;
            return context.chart.scales.y.getPixelForValue(0);
          },
        },
        base: {
          from: (context) => {
            if (context.type !== 'data') return undefined;
            return context.chart.scales.y.getPixelForValue(0);
          },
        },
        ...options.animations,
      };

      if (!options.plugins) {
        options.plugins = {};
      }

      options.plugins.legend = {
        display: this.legendDisplay,
      };

      if (this.tooltipCallback || this.tooltipTitleCallback) {
        options.plugins.tooltip = {
          backgroundColor: this.tooltipBackgroundColor,
          callbacks: {
            title: this.tooltipTitleCallback,
            label: this.tooltipCallback,
          },
        };
      }

      return options;
    },
    computedChartData() {
      const data = { ...this.chartData };
      data.datasets = data.datasets.map((dataset) => ({
        ...dataset,
        barThickness: this.barThickness,
        maxBarThickness: this.maxBarThickness,
        borderRadius: this.borderRadius,
        backgroundColor: dataset.backgroundColor || this.barBackgroundColor,
      }));
      return data;
    },
  },
};
</script>

<style scoped>
.chart-container {
  position: relative;
  display: flex;
}
.stack-top {
  position: absolute;
  display: flex;
  inset: 0;
}
.hidden-caption {
  display: none;
}
</style>
